import React, { FC, useEffect, useMemo } from 'react';
import { Form } from 'antd';
import { debounce } from 'lodash';
import { MpContractValue } from 'src/models/matchingProfiles/types';
import ContractValueFilter from 'src/shared/Tenders/ContractValueFilter';
import { useApiTenders } from 'src/models/procurements/Tenders/hooks';
import { GenericSearchDropdown, IGenericSearchDropdownProps } from 'src/shared';
import styles from './index.module.scss';
import { useTranslation } from 'react-i18next';

type FormState = MpContractValue;
const ContractValueQuickFilter: FC<IGenericSearchDropdownProps> = props => {
  const { t } = useTranslation();
  const [{ vars }, updateSearchArgs] = useApiTenders();
  const [form] = Form.useForm<FormState>();

  const initialValues = useMemo(
    () => ({
      min: vars.contractValueRange?.min,
      max: vars.contractValueRange?.max,
      currency: vars.contractValueRange?.currency,
      includeMissingValue: vars.contractValueIncludeMissingValue
    }),
    [
      vars.contractValueIncludeMissingValue,
      vars.contractValueRange?.currency,
      vars.contractValueRange?.max,
      vars.contractValueRange?.min
    ]
  );

  const onUpdateValue = useMemo(
    () =>
      debounce((_, values: FormState) => {
        const { min, max, currency, includeMissingValue } = values;
        updateSearchArgs({
          contractValueRange: { min, max, currency },
          contractValueIncludeMissingValue: includeMissingValue
        });
      }, 300),
    [updateSearchArgs]
  );

  const isChanged = useMemo(
    () =>
      !!vars.contractValueRange?.currency ||
      !!vars.contractValueRange?.max ||
      !!vars.contractValueRange?.min ||
      !!vars.contractValueIncludeMissingValue,
    [
      vars.contractValueIncludeMissingValue,
      vars.contractValueRange?.currency,
      vars.contractValueRange?.max,
      vars.contractValueRange?.min
    ]
  );

  useEffect(() => {
    if (
      vars.contractValueRange?.min !== form.getFieldValue('min') ||
      vars.contractValueRange?.max !== form.getFieldValue('max') ||
      vars.contractValueRange?.currency !== form.getFieldValue('currency') ||
      vars.contractValueIncludeMissingValue !== form.getFieldValue('includeMissingValue')
    ) {
      form.setFieldsValue({
        min: vars.contractValueRange?.min,
        max: vars.contractValueRange?.max,
        currency: vars.contractValueRange?.currency,
        includeMissingValue: vars.contractValueIncludeMissingValue
      });
    }
  }, [
    form,
    vars.contractValueIncludeMissingValue,
    vars.contractValueRange?.currency,
    vars.contractValueRange?.max,
    vars.contractValueRange?.min
  ]);
  return (
    <GenericSearchDropdown
      {...props}
      label={t('ExpiringContracts.estimatedContractValue')}
      overlay={
        <Form form={form} onValuesChange={onUpdateValue} initialValues={initialValues}>
          <ContractValueFilter min={vars.contractValueRange?.min} max={vars.contractValueRange?.max} />
        </Form>
      }
      overlayClassName={styles.overlay}
      isChanged={isChanged}
      special={props.special ? props.special : !vars.profileId && isChanged}
    />
  );
};

export default ContractValueQuickFilter;
